import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ROUTES } from '@/config/routes';
import { HottrMark } from '@/components/brand/HottrMark';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import { PageShell } from '@/components/layout/PageShell';
import { useProfiles } from '@/modules/profiles/useProfiles';
import { pickFeaturedProfile } from '@/modules/profiles/catalogue';
import { useAuth } from '@/modules/auth/useAuth';
import { useProfileBuilder } from '@/modules/auth/useProfileBuilder';
import { beginPassportLogin, isPassportEnabled } from '@/auth/passport';

const EASE = [0.19, 1, 0.22, 1] as const;

type Phase = 'brand' | 'editorial' | 'final';
type View = 'landing' | 'login' | 'signup';

const reducedMotion = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export const LandingPage = () => {
  const { profiles } = useProfiles();
  const featured = useMemo(() => pickFeaturedProfile(profiles), [profiles]);

  const navigate = useNavigate();
  const { user, logIn, signUp, listFactors } = useAuth();
  const { isComplete } = useProfileBuilder();

  const [phase, setPhase] = useState<Phase>(() =>
    reducedMotion() ? 'final' : 'brand',
  );
  const [view, setView] = useState<View>('landing');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Three-state entry sequence: brand hold → editorial reveal → final
  useEffect(() => {
    if (phase === 'brand') {
      const t = setTimeout(() => setPhase('editorial'), 700);
      return () => clearTimeout(t);
    }
    if (phase === 'editorial') {
      const t = setTimeout(() => setPhase('final'), 900);
      return () => clearTimeout(t);
    }
  }, [phase]);

  useEffect(() => {
    if (!user) return;
    if (isComplete) navigate(ROUTES.feed, { replace: true });
    else navigate(ROUTES.profileBuilder, { replace: true });
  }, [user, isComplete, navigate]);

  const goTo = (v: View) => {
    setEmail('');
    setPassword('');
    setError(null);
    setSubmitting(false);
    setView(v);
  };

  const startPassport = async (returnTo: string = ROUTES.profileBuilder) => {
    setError(null);
    setSubmitting(true);
    try {
      await beginPassportLogin(returnTo);
    } catch (err) {
      setSubmitting(false);
      setError(err instanceof Error ? err.message : 'Could not start account sign-in.');
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res =
      view === 'login'
        ? await logIn(email, password)
        : await signUp(email, password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? 'Something went wrong.');
      return;
    }
    // Post-password check: if the account has TOTP enrolled, Supabase
    // returned an AAL1 session. We must step up to AAL2 before any
    // protected route is reachable. Pop the challenge page; the redirect
    // useEffect below stays parked at AAL1 until /auth/mfa elevates.
    if (view === 'login') {
      const factors = await listFactors();
      const verified = factors.find((f) => f.status === 'verified');
      if (verified) {
        navigate(ROUTES.mfaChallenge, { state: { factorId: verified.id, next: ROUTES.feed } });
      }
    }
  };

  const showImage = phase !== 'brand';
  const isFinal = phase === 'final';

  return (
    <PageShell bare>
      <main className="relative h-dvh w-full overflow-hidden bg-ink text-text-primary">

        {/* CINEMATIC IMAGE — fades in during 'editorial' phase */}
        <motion.div
          aria-hidden
          className="absolute inset-0"
          animate={{ opacity: showImage ? 1 : 0 }}
          initial={{ opacity: showImage ? 1 : 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <EditorialImage
            scene={featured?.scene ?? 'club_room'}
            seed={featured?.sceneSeed ?? 1}
            photoUrl={featured?.photos?.[0]}
            aspect="aspect-auto"
            mono
            className="h-full"
          />
        </motion.div>

        {/* EDITORIAL SCRIM — rises from solid ink at bottom */}
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          animate={{ opacity: showImage ? 1 : 0 }}
          initial={{ opacity: showImage ? 1 : 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          style={{
            background:
              'linear-gradient(to top, rgba(5,6,8,0.92) 0%, rgba(5,6,8,0.4) 55%, rgba(5,6,8,0.1) 100%)',
          }}
        />

        {/* BRAND ARRIVAL — large centered logo, exits to reveal editorial image */}
        <AnimatePresence>
          {phase === 'brand' && (
            <motion.div
              key="brand-arrival"
              aria-hidden
              className="absolute inset-0 z-50 flex items-center justify-center bg-ink"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                <HottrMark size="text-7xl" surface=".app" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* MASTHEAD — fades in with editorial image */}
        <motion.header
          className="relative z-10 flex items-center justify-between gap-4 px-6 pt-6 md:px-12 md:pt-8"
          animate={{ opacity: showImage ? 1 : 0 }}
          initial={{ opacity: showImage ? 1 : 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          style={{
            paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
            pointerEvents: showImage ? 'auto' : 'none',
          }}
        >
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => goTo('landing')}
              className="flex items-center"
              aria-label="HOTTR — back to start"
            >
              <HottrMark size="text-2xl md:text-[28px]" surface=".app" />
            </button>
            {/* No switcher pills here. This masthead already carries the
                wordmark, and the old AppSwitcher rendered a second HOTTR
                lockup an inch from it (pre-ship check 2). The front door is
                also pre-entry — Screen System §6 keeps the chassis out until
                you are in, so cross-world navigation belongs after sign-in,
                where PageShell and the header now carry it. */}
          </div>
          <span
            aria-hidden
            className="hidden font-display text-[10px] font-bold uppercase tracking-widest text-text-secondary md:inline"
          >
            Issue 01 · Melbourne · MMXXVI
          </span>
        </motion.header>

        {/* VERTICAL ROTATED MARKER — desktop only */}
        <motion.span
          aria-hidden
          className="absolute right-8 top-32 hidden font-display text-[9px] font-bold uppercase tracking-[0.5em] text-text-muted lg:block"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          animate={{ opacity: isFinal ? 1 : 0 }}
          initial={{ opacity: isFinal ? 1 : 0 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          HOTTR · NIGHT FILE No. 01 · ATTRACTION FIRST
        </motion.span>

        {/* BOTTOM SECTION — slides up at 'final' phase */}
        <motion.div
          className="absolute inset-x-0 bottom-0 z-10 px-6 pb-10 md:px-12 md:pb-14"
          animate={{ opacity: isFinal ? 1 : 0, y: isFinal ? 0 : 18 }}
          initial={{ opacity: isFinal ? 1 : 0, y: isFinal ? 0 : 18 }}
          transition={{ duration: 0.5, ease: EASE }}
          style={{
            paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))',
            pointerEvents: isFinal ? 'auto' : 'none',
          }}
        >
          <AnimatePresence mode="wait">

            {view === 'landing' && (
              <motion.div
                key="landing"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4, ease: EASE }}
              >
                {/* FIXED EDITORIAL COPY */}
                <h1
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 800,
                    fontSize: 'clamp(2.4rem, 8vw, 4rem)',
                    lineHeight: 1.05,
                    letterSpacing: '-0.03em',
                    color: '#FFFFFF',
                    textTransform: 'uppercase',
                  }}
                >
                  ATTRACTION IS REAL.
                </h1>
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 600,
                    fontSize: 'clamp(0.9rem, 3vw, 1.1rem)',
                    letterSpacing: 0,
                    color: '#FF0CB6',
                    marginTop: '0.5rem',
                  }}
                >
                  Labels aren't.
                </p>

                {/* CTAs — Passport first when enabled; email paths secondary */}
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {isPassportEnabled() ? (
                    <>
                      <PrimaryCTA
                        onClick={() => void startPassport(ROUTES.profileBuilder)}
                        disabled={submitting}
                      >
                        {submitting ? 'Signing you in…' : 'Continue with your HOTTR account'}
                      </PrimaryCTA>
                      <SecondaryCTA onClick={() => goTo('login')}>Use email</SecondaryCTA>
                    </>
                  ) : (
                    <>
                      <PrimaryCTA onClick={() => goTo('signup')}>Sign up</PrimaryCTA>
                      <SecondaryCTA onClick={() => goTo('login')}>Log in</SecondaryCTA>
                    </>
                  )}
                </div>
                {error && view === 'landing' && (
                  <div className="mt-4 max-w-md">
                    <ErrorBanner>{error}</ErrorBanner>
                  </div>
                )}
              </motion.div>
            )}

            {view === 'login' && (
              <motion.div
                key="login"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4, ease: EASE }}
              >
                <GlassPanel>
                  <FormLabel>Log in</FormLabel>
                  {isPassportEnabled() && (
                    <div className="mt-5 space-y-3">
                      <PrimaryCTA
                        onClick={() => void startPassport(ROUTES.profileBuilder)}
                        disabled={submitting}
                        fullWidth
                      >
                        {submitting ? 'Signing you in…' : 'Continue with your HOTTR account'}
                      </PrimaryCTA>
                      <p className="text-center font-display text-[10px] font-bold uppercase tracking-widest text-white/35">
                        or use email
                      </p>
                    </div>
                  )}
                  <form onSubmit={submit} className="mt-5 grid gap-4" noValidate>
                    <GlassField
                      label="Email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      required
                      value={email}
                      placeholder="you@example.com"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <GlassField
                      label="Password"
                      type="password"
                      name="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      placeholder="••••••••"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    {error && <ErrorBanner>{error}</ErrorBanner>}
                    <div className="flex items-center gap-4 pt-1">
                      <FormSubmit disabled={submitting}>
                        {submitting ? 'Logging in...' : 'Log in'}
                      </FormSubmit>
                      <BackButton onClick={() => goTo('landing')}>← Back</BackButton>
                    </div>
                  </form>
                  <p className="mt-5 text-[0.75rem] text-white/40">
                    New here?{' '}
                    <button
                      type="button"
                      onClick={() => goTo('signup')}
                      className="text-white/70 transition-colors duration-[200ms] hover:text-white"
                    >
                      Sign up.
                    </button>
                  </p>
                  <p className="mt-2 text-[0.75rem] text-white/40">
                    Forgot your password?{' '}
                    <Link
                      to={ROUTES.forgot}
                      className="text-white/70 transition-colors duration-[200ms] hover:text-white"
                    >
                      Reset it.
                    </Link>
                  </p>
                </GlassPanel>
              </motion.div>
            )}

            {view === 'signup' && (
              <motion.div
                key="signup"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4, ease: EASE }}
              >
                <GlassPanel>
                  <FormLabel>Sign up</FormLabel>
                  {isPassportEnabled() && (
                    <div className="mt-5 space-y-3">
                      <PrimaryCTA
                        onClick={() => void startPassport(ROUTES.profileBuilder)}
                        disabled={submitting}
                        fullWidth
                      >
                        {submitting ? 'Signing you in…' : 'Continue with your HOTTR account'}
                      </PrimaryCTA>
                      <p className="text-center font-display text-[10px] font-bold uppercase tracking-widest text-white/35">
                        or create with email
                      </p>
                    </div>
                  )}
                  <form onSubmit={submit} className="mt-5 grid gap-4" noValidate>
                    <GlassField
                      label="Email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      required
                      value={email}
                      placeholder="you@example.com"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <GlassField
                      label="Password"
                      type="password"
                      name="password"
                      autoComplete="new-password"
                      required
                      value={password}
                      placeholder="••••••••"
                      hint="8 characters minimum."
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    {error && <ErrorBanner>{error}</ErrorBanner>}
                    <div className="flex items-center gap-4 pt-1">
                      <FormSubmit disabled={submitting}>
                        {submitting ? 'Creating account...' : 'Sign up'}
                      </FormSubmit>
                      <BackButton onClick={() => goTo('landing')}>← Back</BackButton>
                    </div>
                    <p
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.3)',
                      }}
                    >
                      18+ only.
                    </p>
                  </form>
                  <p className="mt-4 text-[0.75rem] text-white/40">
                    Already inside?{' '}
                    <button
                      type="button"
                      onClick={() => goTo('login')}
                      className="text-white/70 transition-colors duration-[200ms] hover:text-white"
                    >
                      Log in.
                    </button>
                  </p>
                </GlassPanel>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>
      </main>
    </PageShell>
  );
};

/* ─── Landing CTAs — pill shape, premium editorial ─── */

const PrimaryCTA = ({
  onClick,
  children,
  disabled,
  fullWidth,
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 800,
      fontSize: '0.7rem',
      letterSpacing: '0.05em',
      padding: '14px 32px',
      borderRadius: '2px',
      background: disabled ? 'rgba(255,12,182,0.4)' : '#FF0CB6',
      color: '#FFFFFF',
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 200ms ease, transform 150ms ease',
      textTransform: 'uppercase',
      lineHeight: 1,
      minHeight: '48px',
      width: fullWidth ? '100%' : undefined,
      boxShadow: disabled ? 'none' : '0 0 15px rgba(255,12,182,0.4)',
      opacity: disabled ? 0.7 : 1,
    }}
    onMouseEnter={(e) => {
      if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = '#D4008E';
    }}
    onMouseLeave={(e) => {
      if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = '#FF0CB6';
    }}
    onMouseDown={(e) => {
      if (!disabled) (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.97)';
    }}
    onMouseUp={(e) => {
      (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
    }}
  >
    {children}
  </button>
);

const SecondaryCTA = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 800,
      fontSize: '0.7rem',
      letterSpacing: '0.05em',
      padding: '14px 32px',
      borderRadius: '2px',
      background: 'transparent',
      color: '#FFFFFF',
      border: '1px solid rgba(255,255,255,0.25)',
      cursor: 'pointer',
      transition: 'border-color 200ms ease, transform 150ms ease',
      textTransform: 'uppercase',
      lineHeight: 1,
      minHeight: '48px',
    }}
    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.6)'; }}
    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.25)'; }}
    onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.97)'; }}
    onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
  >
    {children}
  </button>
);

/* ─── Form glass panel ─── */

const GlassPanel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      background: 'rgba(5,6,8,0.75)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '2px',
      padding: '32px',
      width: '100%',
      maxWidth: '400px',
    }}
  >
    {children}
  </div>
);

const FormLabel = ({ children }: { children: React.ReactNode }) => (
  <p
    style={{
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 700,
      fontSize: '0.7rem',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.48)',
    }}
  >
    {children}
  </p>
);

interface GlassFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}

const GlassField = ({ label, hint, ...rest }: GlassFieldProps) => (
  <label className="block">
    <span
      style={{
        display: 'block',
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.48)',
        marginBottom: '8px',
      }}
    >
      {label}
    </span>
    <input
      {...rest}
      style={{
        display: 'block',
        width: '100%',
        height: '52px',
        padding: '0 16px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '2px',
        color: '#FFFFFF',
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 400,
        fontSize: '0.9rem',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 150ms ease',
      }}
      className="glass-field"
      onFocus={(e) => { e.currentTarget.style.borderColor = '#FF0CB6'; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
    />
    {hint && (
      <span
        style={{
          display: 'block',
          marginTop: '6px',
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.3)',
        }}
      >
        {hint}
      </span>
    )}
  </label>
);

const FormSubmit = ({ children, disabled }: { children: React.ReactNode; disabled: boolean }) => (
  <button
    type="submit"
    disabled={disabled}
    style={{
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 800,
      fontSize: '0.7rem',
      letterSpacing: '0.05em',
      padding: '14px 32px',
      borderRadius: '2px',
      background: disabled ? 'rgba(255,12,182,0.4)' : '#FF0CB6',
      color: '#FFFFFF',
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 200ms ease',
      textTransform: 'uppercase',
      lineHeight: 1,
      minHeight: '48px',
      opacity: disabled ? 0.6 : 1,
      boxShadow: disabled ? 'none' : '0 0 15px rgba(255,12,182,0.4)',
    }}
    onMouseEnter={(e) => {
      if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = '#D4008E';
    }}
    onMouseLeave={(e) => {
      if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = '#FF0CB6';
    }}
  >
    {children}
  </button>
);

const BackButton = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 700,
      fontSize: '0.75rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.35)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      transition: 'color 150ms ease',
    }}
    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.7)'; }}
    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.35)'; }}
  >
    {children}
  </button>
);

const ErrorBanner = ({ children }: { children: React.ReactNode }) => (
  <p
    style={{
      padding: '10px 14px',
      background: 'rgba(255,12,182,0.08)',
      border: '1px solid rgba(255,12,182,0.3)',
      borderRadius: '2px',
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 700,
      fontSize: '0.7rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#FF0CB6',
    }}
  >
    — {children}
  </p>
);
